import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUserLanguage, saveUserLanguage } from '../utils/storage';
import { useAuth } from '../contexts/AuthContext';
import { getEffectiveTheme, setTheme } from '../utils/theme';
import BackButton from '../components/BackButton';
import Icon from '../components/Icon';
import './styles/SettingsScreen.css';

const SettingsScreen = () => {
  const { t, i18n } = useTranslation();
  const { user, signInWithGoogle, signOut, deleteAccount, syncData } = useAuth();
  const [language, setLanguage] = useState('en');
  const [theme, setThemeState] = useState(getEffectiveTheme());

  useEffect(() => {
    setLanguage(getUserLanguage());
  }, []);

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    setLanguage(lang);
    saveUserLanguage(lang);
    i18n.changeLanguage(lang);
  };

  const handleSignOut = async () => {
    if (window.confirm(t('confirmSignOut'))) {
      await signOut();
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm(t('confirmDelete'))) {
      await deleteAccount();
    }
  };

  const chooseTheme = (t) => {
    setTheme(t);
    setThemeState(t);
  };

  return (
    <div className="settings-container">
      <BackButton to="/" label={t('home')} />
      <h2 className="settings-title">{t('settings')}</h2>

      {/* Account Section */}
      <div className="settings-section">
        <h3 className="settings-section-title">{t('account')}</h3>
        {user ? (
          <>
            <div className="settings-row">
              <div className="settings-row-icon">
                {user.photoURL
                  ? <img src={user.photoURL} alt="" className="user-avatar" referrerPolicy="no-referrer" />
                  : <Icon name="user" size={20} />}
              </div>
              <div className="settings-row-content">
                <span className="settings-label">{user.displayName || user.email}</span>
                {user.displayName && <span className="user-email">{user.email}</span>}
              </div>
            </div>
            <button className="settings-row clickable" onClick={syncData}>
              <div className="settings-row-icon"><Icon name="cloud" size={20} /></div>
              <span className="settings-label">{t('syncData')}</span>
              <span className="settings-arrow">&rsaquo;</span>
            </button>
            <button className="settings-row clickable" onClick={handleSignOut}>
              <div className="settings-row-icon"><Icon name="logout" size={20} /></div>
              <span className="settings-label">{t('signOut')}</span>
              <span className="settings-arrow">&rsaquo;</span>
            </button>
            <button className="settings-row clickable" onClick={handleDeleteAccount}>
              <div className="settings-row-icon"><Icon name="warning" size={20} /></div>
              <span className="settings-label settings-label-danger">{t('deleteAccount')}</span>
              <span className="settings-arrow">&rsaquo;</span>
            </button>
          </>
        ) : (
          <div className="settings-row">
            <button className="settings-btn google-btn" onClick={signInWithGoogle}>
              <svg width="18" height="18" viewBox="0 0 48 48" style={{marginRight: 8, verticalAlign: 'middle'}}>
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              {t('signInWithGoogle')}
            </button>
          </div>
        )}
      </div>

      {/* Appearance Section */}
      <div className="settings-section">
        <h3 className="settings-section-title">{t('appearance')}</h3>
        <div className="settings-row" role="group" aria-label={t('theme')}>
          <div className="settings-row-icon">
            <Icon name={theme === 'light' ? 'sun' : 'moon'} size={20} />
          </div>
          <span className="settings-label">{t('theme')}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              className="settings-btn"
              aria-pressed={theme === 'light'}
              onClick={() => chooseTheme('light')}
              style={{ opacity: theme === 'light' ? 1 : 0.55 }}
            >{t('light')}</button>
            <button
              className="settings-btn"
              aria-pressed={theme === 'dark'}
              onClick={() => chooseTheme('dark')}
              style={{ opacity: theme === 'dark' ? 1 : 0.55 }}
            >{t('dark')}</button>
          </div>
        </div>
      </div>

      {/* Language Section */}
      <div className="settings-section">
        <h3 className="settings-section-title">{t('language')}</h3>
        <div className="settings-row">
          <div className="settings-row-icon"><Icon name="globe" size={20} /></div>
          <span className="settings-label">{t('translationLanguage')}</span>
          <select className="settings-select" value={language} onChange={handleLanguageChange}>
            <option value="en">English</option>
            <option value="ru">Русский (Russian)</option>
            <option value="ml">Malayalam</option>
            <option value="es">Spanish</option>
          </select>
        </div>
      </div>

      {/* About Section */}
      <div className="settings-section">
        <h3 className="settings-section-title">{t('about')}</h3>
        <Link to="/aboutus" className="settings-link-row">
          <div className="settings-row-icon"><Icon name="info" size={20} /></div>
          <span className="settings-label">{t('aboutUs')}</span>
          <span className="settings-arrow">&rsaquo;</span>
        </Link>
        <Link to="/privacy-policy" className="settings-link-row">
          <div className="settings-row-icon"><Icon name="lock" size={20} /></div>
          <span className="settings-label">{t('privacyPolicy')}</span>
          <span className="settings-arrow">&rsaquo;</span>
        </Link>
        <Link to="/contact" className="settings-link-row">
          <div className="settings-row-icon"><Icon name="mail" size={20} /></div>
          <span className="settings-label">{t('contactUs')}</span>
          <span className="settings-arrow">&rsaquo;</span>
        </Link>
      </div>
    </div>
  );
};

export default SettingsScreen;
